import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getApiUrl } from "@/utils/api";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { z } from "zod";
import { useTranslation } from "react-i18next";

const supplierSchema = z.object({
  name: z.string().min(1, "Name is required"),
});

type SupplierFormValues = z.infer<typeof supplierSchema>;

interface SupplierFormProps {
  id?: number | null;
  onSuccess: () => void;
  onCancel: () => void;
}

export const SupplierForm = ({ id, onSuccess, onCancel }: SupplierFormProps) => {
  const { t } = useTranslation();
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const isEdit = !!id;

  const form = useForm<SupplierFormValues>({
    resolver: zodResolver(supplierSchema),
    defaultValues: {
      name: "",
    },
  });

  // Fetch supplier data if in edit mode
  useQuery({
    queryKey: ["supplier", id],
    queryFn: async () => {
      if (!id) return null;
      const response = await fetch(getApiUrl(`comercializadoras/${id}/`), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error("Failed to fetch supplier");
      }
      const data = await response.json();
      form.reset(data);
      return data;
    },
    enabled: isEdit && !!id,
  });

  const mutation = useMutation({
    mutationFn: async (data: SupplierFormValues) => {
      const url = isEdit ? `comercializadoras/${id}/` : 'comercializadoras/';
      const method = isEdit ? 'PUT' : 'POST';
      
      const response = await fetch(getApiUrl(url), {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to save supplier');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["suppliers"] });
      onSuccess();
    },
  });

  const onSubmit = async (data: SupplierFormValues) => {
    try {
      await mutation.mutateAsync(data);
    } catch (error) {
      // Error is handled by the mutation's onError
      console.error(error);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("suppliers.form.name")}</FormLabel>
              <FormControl>
                <Input
                  placeholder={t("suppliers.form.namePlaceholder")}
                  {...field}
                  disabled={mutation.isPending}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end space-x-4 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={mutation.isPending}
          >
            {t("common.cancel")}
          </Button>
          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending
              ? t("common.saving")
              : isEdit
              ? t("common.update")
              : t("common.save")}
          </Button>
        </div>
      </form>
    </Form>
  );
};
